/**
 * Verification API Client
 * Endpoints for seller verification tier management
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  return token;
};

/**
 * Get verification status for authenticated seller
 */
export const getVerificationStatus = async () => {
  const token = getAuthToken();
  
  const response = await fetch(`${BACKEND_URL}/api/verification/status`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get verification status');
  }
  
  return response.json();
};

/**
 * Apply for verification tier
 * @param {string} requestedTier - T1, T2, or T3
 */
export const applyForVerification = async (requestedTier) => {
  const token = getAuthToken();
  
  const response = await fetch(`${BACKEND_URL}/api/verification/apply`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ requested_tier: requestedTier })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to apply for verification');
  }
  
  return response.json();
};

/**
 * Request verification recheck (rate limited: 3/day)
 */
export const recheckVerification = async () => {
  const token = getAuthToken();
  
  const response = await fetch(`${BACKEND_URL}/api/verification/recheck`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('RATE_LIMIT');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to recheck verification');
  }
  
  return response.json();
};

/**
 * Recompute seller metrics (seller self-serve, rate limited: 3/day)
 */
export const recomputeMetrics = async () => {
  const token = getAuthToken();
  
  const response = await fetch(`${BACKEND_URL}/api/verification/metrics/recompute`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('RATE_LIMIT');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to recompute metrics');
  }
  
  return response.json();
};
